import { Module } from "vuex";
import * as firebase from "firebase/app";
import "firebase/firestore";
import "firebase/functions";
import "firebase/auth";
import router from "@/router";
import { getRegionName } from "@/utils/functionsRegion";
import { defaultFacilityValues } from "@/consts/defaultFacilityValues";
import { loadDataToStore } from "./modulesHelper";
import { CalendarFilters } from "@/models/calendar/CalendarFilters";
import { serializer } from "@/api/shared/serializer";
import { getEnabledRoute } from "@/router/router-helper";
import { isArraysEqual } from "@/utils/isArraysEqual";
import {StaffAccessLevels} from "@/enums/StaffAccessLevels";

const authModule: Module<any, any> = {
  state: {
    user: { name: "" },
    onCurrentUserUnsubscribe: null,
    premiumFeatures: {
      isStaffHubGuestbook: false,
      isStaffHubSurveys: false,
      isStaffHubReporting: false, 
      isStaffHubMaintenance: false,
      isStaffHubConcierge: false,
      isStaffHubLifestylePrograms: false,
      isStaffHubContentManagerMediaCreator: false,
      isStaffHubContentManagerMediaLibrary: false,
      isStaffHubContentManagerDigitalSignage: false,
      isStaffHubDevicesColumnDigitalSignage: false,
      isStaffHubContentManagerGallery: false,
      isStaffHubCalendar: false,
      isStaffHubResidents: false,
      isStaffHubDashboard: false,
      isStaffHubPrintableCalendar: false,
      isStaffHubMessenger: false,
      isStaffHubDocuments: false
    },
    error: null,
    currentFacility: null,
    currentFacilityData: null,
    loginFailed: false,
    facilityLoginFailed: false,
    maintenanceUserFailed: false,
    loadingSuccess: false,
    isOneSubscribeOnResidents: false,
    isChangingFacilityOnCurrentBrowser: false,
    onNotificationsUnsubscribe: null
  },

  mutations: {
    setUser(state, payload) {
      state.user = payload;
    },
    setUserParams(state, params) {
      state.user.email = params.email;
    },
    setError(state, payload) {
      state.error = payload;
    },
    setCurrentFacilityId(state, payload) {
      state.currentFacility = payload;
    },
    setCurrentFacilityData(state, currentFacility) {
      state.currentFacilityData = currentFacility;
      state.premiumFeatures = {
        isStaffHubDashboard: currentFacility.isStaffHubDashboard,
        isStaffHubGuestbook: currentFacility.isStaffHubGuestbook,
        isStaffHubSurveys: currentFacility.isStaffHubSurveys,
        isStaffHubReporting: currentFacility.isStaffHubReporting, 
        isStaffHubMaintenance: currentFacility.isStaffHubMaintenance,
        isStaffHubConcierge: currentFacility.isStaffHubConcierge,
        isStaffHubLifestylePrograms: currentFacility.isStaffHubLifestylePrograms,
        isStaffHubContentManagerMediaCreator: currentFacility.isStaffHubContentManagerMediaCreator,
        isStaffHubContentManagerMediaLibrary: currentFacility.isStaffHubContentManagerMediaLibrary,
        isStaffHubContentManagerDigitalSignage: currentFacility.isStaffHubContentManagerDigitalSignage,
        isStaffHubDevicesColumnDigitalSignage: currentFacility.isStaffHubDevicesColumnDigitalSignage,
        isStaffHubContentManagerGallery: currentFacility.isStaffHubContentManagerGallery,
        isStaffHubCalendar: currentFacility.isStaffHubCalendar,
        isStaffHubResidents: currentFacility.isStaffHubResidents,
        isStaffHubPrintableCalendar: currentFacility.isStaffHubPrintableCalendar,
        isStaffHubMessenger: currentFacility.isStaffHubMessenger,
        isStaffHubDocuments: currentFacility.isStaffHubDocuments
      }
      console.log('premiumFeatures:', state.premiumFeatures)
    },
    setLoginFailed(state, payload) {
      state.loginFailed = payload;
    },
    setFacilityLoginFailed(state, payload) {
      state.facilityLoginFailed = payload;
    },
    setMaintenanceUserFailed(state, payload) {
      state.maintenanceUserFailed = payload;
    },
    setIsChangingFacilityOnCurrentBrowser(state, payload) {
      state.isChangingFacilityOnCurrentBrowser = payload;
    },
    clearCurrentFacilityName(state) {
      state.currentFacilityData.name = "";
    },
    setLoginSuccess(state, payload) {
      state.loadingSuccess = payload;
    },
    clearAuthState(state){
      state.user = null,
      state.premiumFeatures = {
        isStaffHubGuestbook: false,
        isStaffHubSurveys: false,
        isStaffHubReporting: false, 
        isStaffHubMaintenance: false,
        isStaffHubConcierge: false,
        isStaffHubLifestylePrograms: false,
        isStaffHubContentManagerMediaCreator: false,
        isStaffHubContentManagerMediaLibrary: false,
        isStaffHubContentManagerDigitalSignage: false,
        isStaffHubDevicesColumnDigitalSignage: false,
        isStaffHubContentManagerGallery: false,
        isStaffHubCalendar: false,
        isStaffHubResidents: false,
        isStaffHubDashboard: false,
        isStaffHubPrintableCalendar: false,
        isStaffHubMessenger: false,
        isStaffHubDocuments: false
      },
      state.error = null
      state.currentFacility = null
      state.currentFacilityData = null
      state.loginFailed = false
      state.facilityLoginFailed = false
      state.maintenanceUserFailed = false
      state.loadingSuccess = false
      state.isOneSubscribeOnResidents = false
      state.isChangingFacilityOnCurrentBrowser = false
      state.onNotificationsUnsubscribe = null
    }
  },
  actions: {
    async userSignIn({ commit, dispatch, state }, payload) {

      return await firebase
        .auth()
        .signInWithEmailAndPassword(payload.email, payload.password)
        .then(async firebaseUser => {

          if (firebaseUser.user) {
            const { claims } = await firebaseUser.user.getIdTokenResult()
            console.log(claims)

            const user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("users").doc(firebaseUser.user.uid).get()) as any

            if (!user.facilitiesIds || !user.facilitiesIds.length) {
              commit("setFacilityLoginFailed", true);
              commit("setLoginFailed", false);
              console.error('userSignIn: No facilities')
              return false
            }

            if(claims.accessLevel === StaffAccessLevels.MaintenanceUser) {
              console.log("MAINTENACE USER")
              commit("setMaintenanceUserFailed", true);
              commit("setLoginFailed", false);
              console.error('userSignIn: User has "Maintenance User" access level')
              router.push("/login");
              return false
            }

            try {
                await dispatch("initAuthData", { user, claims })

                if (this.getters.isSuperAdmin) {
                  console.log(this.getters.user)
                  router.push("/buddy/facilities")
                } else if(this.getters.isMaintenanceAdmin) {
                  router.push("/maintenanceRequests")
                } else {
                  const enabledRoute = getEnabledRoute(this.getters.premiumFeatures)
                  router.push(enabledRoute.path);
                }
                return true
            } catch (error) {
                commit("setLoginFailed", true);
                console.error('userSignIn: Login Failed for user:' + payload.email + ' with Exception: ' + error)
                return false
            }
          }
        })
        .catch(error => {
          commit("setLoginFailed", true);
          commit("setError", error.message);
          console.error('userSignIn: Unhandled error')
          return false
        });
    },

    async autoSignIn({ commit, dispatch, state }, firebaseUser) {
      if (!firebaseUser) return;
      state.isOneSubscribeOnResidents = false;

      const user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("users").doc(firebaseUser.uid).get()) as any

      if (!user.facilitiesIds || !user.facilitiesIds.length) {
        commit("setFacilityLoginFailed", true);
        commit("setLoginFailed", false);
        console.error('autoSignIn: No facilities')
        router.push("/login");
        return false
      }

      try {
        const { claims } = await firebaseUser.getIdTokenResult()
        const currentUserData = {...state.user}
        await dispatch("initAuthData", { user: currentUserData, claims: claims })

        return true
      } catch (error) {
        commit("setLoginFailed", true);
        console.error('autoSignIn: Login Failed for user:' + user.email + ' with Exception: ' + error)
        return false
      }
    },

    async signInByCustomToken({ commit, dispatch }, payload) {

      const customToken = payload.customToken
      const isExternalPagesViewing = payload.isExternalPagesViewing || false // user sign in by custom token to view external page like 'Set Password Screen' for Family Hub, Notice Board for TV Box etc.

      if (!customToken) {
        commit("setLoginFailed", true);
        console.error('Login Failed for custom token')
        router.push("/login");
        return false
      }

      return await firebase
        .auth()
        .signInWithCustomToken(customToken)
        .then(async (firebaseUser: any) => {
          if (firebaseUser.user) {
            try {
              const { claims } = await firebaseUser.user.getIdTokenResult()
              console.log(claims)

              let user: any = {}
              if(claims?.isFamily === true){
                user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("familyHubUsers").doc(firebaseUser.user.uid).get()) as any
              } else if (claims?.isResident === true) {
                user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("residents").doc(firebaseUser.user.uid).get()) as any
              } else if (claims?.isStaff === true) {
                user = serializer.documentSnapshotToDocument(await firebase.firestore().collection("users").doc(firebaseUser.user.uid).get()) as any
              } else {
                console.error("User doesn't have correct claims")
              }

              user.isExternalPagesViewing = isExternalPagesViewing
              await dispatch("initAuthData", { user, claims })
              return true
            } catch (error) {
              commit("setLoginFailed", true);
              console.error('Login Failed for custom token with Exception: ' + error)
              return false
            }
          }
        })
        .catch(error => {
          commit("setLoginFailed", true);
          console.error('Login Failed for custom token', error)
          router.push("/login");
          return false
        });
    },

    async stopImpersonation({ dispatch }) {
      const impersonation = JSON.parse(localStorage.getItem("impersonation"))
      const superAdminCustomTokenResult = await firebase.app().functions(getRegionName()).httpsCallable("getUserCustomToken")({ userId: impersonation.superUserId })
      const superAdminTokenCustomToken = superAdminCustomTokenResult?.data?.customToken;
      await dispatch("signInByCustomToken", {  customToken: superAdminTokenCustomToken })

      router.push(`/buddy/facilities/${impersonation.selectedBuddyFacilityId}`)
    },

    /**
      @deprecated
      Candidate for removal.
      We are not using anonymous users anymore,
      but potentially it could be used for external meetings (virtual events)
    **/
    async signInAnonymously({ commit, dispatch }, data) {
      return await firebase.auth()
        .signInAnonymously()
        .then(async (query: any) => {
          const userId = query.user.uid;
          const isUserAnonymous = query.user.isAnonymous;

          const user = {
            name: "Guest",
            logoUrl: "",
            isAnonymous: true,
            email: data?.email || null
          };

          if (isUserAnonymous) {
            return await firebase
              .app()
              .functions(getRegionName())
              .httpsCallable("setAnonymouslyClaims")({ userId: userId, facilityId: data?.facilityId })
              .then(async (a) => {
                return await firebase.auth().currentUser.getIdTokenResult(true).then(async (idTokenResult) => {
                  const claims = idTokenResult.claims;
                  console.log(claims)
                  commit("setUser", user);
                  return user;
                })
              });
          }
          else {
            return null
          }
        })
        .catch(error => {
          console.error(error);
          return null
        });
    },

    async changeCurrentUserFacility({ dispatch, commit, state }, { newFacilityId, redirectEnabled }) {
      console.log('changing currentUser facility to: ' + newFacilityId)

      const currentUserId = state.user.id
      const orgId = state.user.orgId
      const currentFacilityId = this.state.authModule.currentFacility
      const isExistFacility = serializer.querySnapshotToDocuments<any>(await firebase.firestore().collection("facility").where("orgId", "==", orgId).get()).some(x => x.id === newFacilityId)

      if (!isExistFacility) {
        console.error('Requested facility not exist in current organisation')
        return false
      }

      return await firebase.app().functions(getRegionName())
        .httpsCallable("changeUsersFacility")({ newFacilityId: newFacilityId, prevFacilityId: currentFacilityId })
        .then(async () => {

          const userSnap = await firebase.firestore().collection("users").doc(currentUserId).get()
          const devicesQuery = await firebase.firestore().collection("devices").where("facilityId", "==", currentFacilityId).where("userId", "==", currentUserId).get()

          return await firebase.firestore().runTransaction(async (transaction: any) => {
            transaction.update(userSnap.ref, { facilityId: newFacilityId });
            if (!devicesQuery.empty) {
              console.log(`updating ${devicesQuery.size} devices`);
              devicesQuery.docs.forEach((doc: any) => {
                transaction.update(doc.ref, { facilityId: newFacilityId });
              })
            }
          })
            .then(async () => {

              if(state.onNotificationsUnsubscribe){
                state.onNotificationsUnsubscribe();
              }

              //getIdToken(true) - to force refresh a token
              return await firebase.auth().currentUser.getIdToken(true).then(async () => {
                return await firebase.auth().currentUser.getIdTokenResult(true).then(async (idTokenResult) => {
                  //get updated claims with new facilityId and update user
                  const claims = idTokenResult.claims;
                  claims.facilityId = newFacilityId
                  const user = this.getters.user
                  user.claims = claims;
                  user.facilityId = newFacilityId
                  console.log('updated current user:', user)
                  commit("setUser", user);
                  commit("setCurrentFacilityId", newFacilityId);
                  commit("clearCurrentFacilityName");
                  commit("setServiceRequestsDashboard", []);
                  commit("setCalenderEventsUpcoming", []);
                  commit("setNotifications", []);
                  dispatch("subscribeOnCurrentFacility", newFacilityId );

                  await dispatch("getResidentsShortData");
                  const facility = await dispatch("getFacility");
                  dispatch("translate", { locale: facility?.languageIso });

                  if (redirectEnabled === true) {
                    const enabledRoute = getEnabledRoute(this.getters.premiumFeatures)
                    window.location.href = enabledRoute.path;
                  }

                  return true
                })
              })

            })
            .catch(error => {
              commit("setIsChangingFacilityOnCurrentBrowser", false);
              console.log(error);
            });
        });
    },

    resetFailures({ dispatch, commit, state }) {
      commit("setLoginFailed", false)
      commit("setFacilityLoginFailed", false)
    },

    async setIsChangingFacilityOnCurrentBrowser({ dispatch, commit, state }, setIsChangingFacilityOnCurrentBrowser) {
      commit("setIsChangingFacilityOnCurrentBrowser", setIsChangingFacilityOnCurrentBrowser);
    },

    async changeUserFacility({ dispatch, commit, state }, { userId, facilityId }) {
      console.log('userId: ' + userId)
      
      return await firebase.app().functions(getRegionName()).httpsCallable("updateUserClaims")({ facilityId: facilityId, userId: userId })
    },

    async getUser({ commit, state }) {
      return await firebase
        .firestore()
        .collection("users")
        .doc(state.user.id)
        .get()
        .then(async (querySnapshot) => {
          const user = querySnapshot?.data();
          user.id = querySnapshot.id;
          return await firebase
            .auth()
            .currentUser.getIdTokenResult()
            .then(function (idTokenResult) {
              user.claims = idTokenResult.claims;
              commit("setUser", user);
              return user
            });
        });
    },

    setUserData({ commit }, user) {
      commit("setUser", user);
    },

    updateUserData({ commit, dispatch }, data) {
      if (data) {
        commit("setUserParams", data);
      }
    },

    userSignOut({ commit, dispatch }, data) {
      const currentUser = firebase.auth().currentUser
      if (currentUser && currentUser.isAnonymous) {
        currentUser.delete()
      }
      firebase.auth().signOut();
      localStorage.clear();
      dispatch("unsubscribeOnResidentsCollection");
      dispatch("unsubscribeOnCalenderEvents");
      dispatch("unsubscribeOnCurrentFacility");
      dispatch("unsubscribeOnCurrentUser");
      dispatch("unsubscribeOnNotifications");
      dispatch("clearBuddyState");
      commit("clearAuthState");

      if (!data?.noRedirect) {
        router.push("/login");
      }
    },

    userSignOutAnonymously({ commit, dispatch }) {
      const currentUser = firebase.auth().currentUser
      if (currentUser && currentUser.isAnonymous) {
        currentUser.delete()
      }
      firebase.auth().signOut();
      localStorage.clear();
      commit("setUser", null);
      commit("setCurrentFacilityId", null);
      commit("setLoginSuccess", false);
      dispatch("unsubscribeOnResidentsCollection");
      dispatch("unsubscribeOnCalenderEvents");
      dispatch("unsubscribeOnCurrentFacility");
      dispatch("unsubscribeOnCurrentUser");
      dispatch("unsubscribeOnNotifications");
    },

    async initAuthData({ commit, dispatch, state }, { user, claims }) {
      commit("setFacilityLoginFailed", false);
      user.claims = claims
      commit("setUser", user);

      if (!state.onResidentsUnsubscribe && !state.isOneSubscribeOnResidents && state.loadingSuccess) {
        state.isOneSubscribeOnResidents = true;
        dispatch("subscribeOnResidentsCollection");
      }

      const currentFacility = serializer.documentSnapshotToDocument(await firebase.firestore().collection("facility").doc(user.facilityId).get()) as any
      currentFacility.dateFormat = currentFacility.dateFormat || defaultFacilityValues.dateFormat
      currentFacility.timeFormat = currentFacility.timeFormat || defaultFacilityValues.timeFormat
      currentFacility.languageIso = currentFacility.languageIso || defaultFacilityValues.locale

      await dispatch("translate", { locale: currentFacility.languageIso });
      commit("setCurrentFacilityId", currentFacility.id);
      commit("setCurrentFacilityData", currentFacility);
      commit("setLoginSuccess", true)
      commit("setMainCalendarFilters", new CalendarFilters());
      dispatch("subscribeOnCurrentFacility", currentFacility.id);
      dispatch("subscribeOnCurrentUser");

      if(!user.isExternalPagesViewing){
        loadDataToStore()
      }
      //dispatch("getCareAPIKey ", currentFacility.id);
      commit("setLoginSuccess", true)
    },
    
    async subscribeOnCurrentUser({ commit, state }) {

      // Check if an existing unsubscribe function is stored in state
      if (state.onCurrentUserUnsubscribe !== null || !state?.user?.id) {
        console.log("already subscribed on current user")
        return;
      }

      const currentUser = state.user
      const query = firebase.firestore().collection("users").doc(state.user.id);

      console.log("Subscribe on current user")
    
      // Create and store the unsubscribe function
      state.onCurrentUserUnsubscribe = await query.onSnapshot((snapshot) => {
        const document = snapshot?.data();
        if (document) {
          document.id = snapshot.id
          if(currentUser.name !== document.name 
            || currentUser.email !== document.email
            || currentUser.facilityId !== document.facilityId
            || currentUser.accessLevel !== document.accessLevel
            || currentUser.regionId !== document.regionId
            || !isArraysEqual(currentUser.facilitiesIds, document.facilitiesIds)){
            commit("setUser", document)
            currentUser.facilityId = document.facilityId;
            console.log(`user ${document?.id} updated`)
          }
        }
      })
    },

    async unsubscribeOnCurrentUser({state}) {
      if (state.onCurrentUserUnsubscribe) {
        state.onCurrentUserUnsubscribe()
        state.onCurrentUserUnsubscribe = null
        console.log("Unsubscribed from current user")
      }
    },

    async unsubscribeOnNotifications({ commit, state }) {
      if(state.onNotificationsUnsubscribe){
        state.onNotificationsUnsubscribe();
        state.onNotificationsUnsubscribe = null;
        console.log("Unsubscribed from notifications")
      }
    }
  },

  getters: {
    user(state) {
      return state.user;
    },

    premiumFeatures(state) {
      return state.premiumFeatures;
    },

    isAuthenticated(state) {
      return (
        state.user !== null &&
        state.user !== undefined &&
        state.user?.name !== null &&
        state.user?.name?.length > 1 &&
        state.loadingSuccess === true
      );
    },

    isImpersonationMode(state) {
      return state?.user?.claims?.impersonating === true
    },

    isSuperAdmin(state) {
      return state?.user?.claims?.accessLevel == "SuperAdmin";
    },

    isAnonymous(state) {
      return state.user.isAnonymous;
    },

    isExternalPagesViewing(state) {
      return state.user?.isExternalPagesViewing;
    },

    isMaintenanceAdmin(state) {
      return state?.user?.claims?.accessLevel === StaffAccessLevels.MaintenanceAdmin;
    },
  }
};
export default authModule;
